// src/ConsumerApp/Screens/Account/views/RfqsView.js

import React, { useState, useMemo, useEffect } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import ClipboardText from '../../../../assets/icons/PClipboardText.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import Phone from '../../../../assets/icons/Phone.svg';
import ChatCircle from '../../../../assets/icons/ChatCircle.svg';
import Receipt from '../../../../assets/icons/Receipt.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

// Custom Hooks
import { useGetRfqsByCustomer, useGetRfqById } from '../../../api/rfq/rfq.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function RfqsView() {
  // 1. Session Context
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. Active Selection State
  const [selectedRfqId, setSelectedRfqId] = useState(null);

  // 3. API Queries
  const {
    data: rawRfqData = [],
    isLoading: isLoadingList,
    refetch: refetchList,
    isRefetching,
  } = useGetRfqsByCustomer({ customerId, rfqs: 'all' });

  const rfqsList = useMemo(() => {
    if (Array.isArray(rawRfqData)) return rawRfqData;
    if (Array.isArray(rawRfqData?.data)) return rawRfqData.data;
    return [];
  }, [rawRfqData]);

  // Set initial selected RFQ when list loads
  useEffect(() => {
    if (rfqsList.length > 0 && !selectedRfqId) {
      setSelectedRfqId(rfqsList[0]._id || rfqsList[0].id);
    }
  }, [rfqsList, selectedRfqId]);

  // Fetch active RFQ details
  const { data: selectedRfqDetails, isLoading: isLoadingDetails } =
    useGetRfqById(selectedRfqId);

  // Fallback / Normalized Detail Fields[cite: 48]
  const activeRfq = selectedRfqDetails || rfqsList.find((r) => (r._id || r.id) === selectedRfqId);
  const items = Array.isArray(activeRfq?.items) ? activeRfq.items : [];
  const totalAmount = Number(activeRfq?.total || activeRfq?.estimated_total || 0);
  const isQuoteReceived = Boolean(activeRfq?.quotation_id || activeRfq?.is_live_rfq);

  // Formatters[cite: 49]
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>My RFQs</h2>
          <span style={styles.headerSubtitle}>
            Track commercial quote requests, supplier estimations, and bulk bids
          </span>
        </div>

      {/*   <button
          type="button"
          style={styles.refreshBtn}
          onClick={() => refetchList()}
          disabled={isRefetching}
        >
          <img
            src={ClockCounterClockwise}
            alt="refresh"
            style={{
              width: 14,
              height: 14,
              opacity: 0.7,
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>{isRefetching ? 'Refreshing...' : 'Refresh'}</span>
        </button> */}
      </div>

      {/* ── Master-Detail Grid ── */}
      {isLoadingList ? (
        <div style={styles.loadingBox}>
          <span>Fetching your RFQ records...</span>
        </div>
      ) : rfqsList.length === 0 ? (
        <div style={styles.emptyBox}>
          <div style={styles.emptyIconCircle}>
            <img
              src={ClipboardText}
              alt="rfq"
              style={{
                width: 24,
                height: 24,
                filter:
                  'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
              }}
            />
          </div>
          <h4 style={styles.emptyTitle}>No RFQs</h4>
          <p style={styles.emptySubtitle}>
            When you request volume quotes or custom specifications, they will be listed here.
          </p>
        </div>
      ) : (
        <div style={styles.splitLayout}>
          {/* Left Master Pane: RFQ Cards List[cite: 49, 53] */}
          <div style={styles.masterListPane}>
            <span style={styles.listSectionLabel}>All Requests ({rfqsList.length})</span>

            <div style={styles.cardsStack}>
              {rfqsList.map((item, idx) => {
                const itemId = item._id || item.id || `rfq-${idx}`;
                const isSelected = selectedRfqId === itemId;
                const rfqNumber = item.rfq_number || `#${String(itemId).slice(-6).toUpperCase()}`;
                const title =
                  item.product_categories?.[0]?.product_category_name ||
                  item.title ||
                  'Bulk Procurement Request';
                const itemCount = item.total_items ?? item.items?.length ?? 1;
                const amount = item.total || item.estimated_total || item.final_total;

                return (
                  <div
                    key={itemId}
                    style={{
                      ...styles.rfqCard,
                      borderColor: isSelected ? '#C7061F' : '#CBD5E1',
                      backgroundColor: isSelected ? '#FFF8F8' : '#FFFFFF',
                      boxShadow: isSelected ? '0 0 0 1px #C7061F' : 'none',
                    }}
                    onClick={() => setSelectedRfqId(itemId)}
                  >
                    <div style={styles.rfqCardTop}>
                      <span style={styles.rfqIdBadge}>{rfqNumber}</span>
                      <div
                        style={{
                          ...styles.statusTag,
                          backgroundColor: item.quotation_id ? '#FFF1F2' : '#F8FAFC',
                          color: item.quotation_id ? '#C7061F' : '#64748B',
                        }}
                      >
                        {item.quotation_id ? 'Quote Received' : item.status || 'Request Sent'}
                      </div>
                    </div>

                    <span style={styles.rfqCardDate}>{formatDate(item.createdAt)}</span>

                    <div style={styles.rfqCardBottom}>
                      <span style={styles.rfqCardTitle}>
                        {title} · {itemCount} {itemCount === 1 ? 'item' : 'items'}
                      </span>
                      {amount && (
                        <strong style={styles.rfqCardAmount}>
                          ₹{Number(amount).toLocaleString('en-IN')}
                        </strong>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Detail Pane: Selected RFQ View[cite: 48] */}
          <div style={styles.detailPane}>
            {isLoadingDetails ? (
              <div style={styles.detailLoading}>
                <span>Loading request details...</span>
              </div>
            ) : !activeRfq ? (
              <div style={styles.detailEmpty}>
                <span>Select an RFQ from the list to view its complete specification</span>
              </div>
            ) : (
              <div style={styles.detailCard}>
                {/* Header Row */}
                <div style={styles.detailHeader}>
                  <div>
                    <h3 style={styles.detailTitle}>
                      {activeRfq.rfq_number || `#${String(selectedRfqId).slice(-6).toUpperCase()}`}
                    </h3>
                    <span style={styles.detailDate}>{formatDate(activeRfq.createdAt)}</span>
                  </div>

                  <div style={styles.detailActionGroup}>
                    <a
                      href="https://wa.me/916304059302"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.detailHelpBtn}
                    >
                      <img src={ChatCircle} alt="chat" style={{ width: 14, height: 14 }} />
                      <span>Chat</span>
                    </a>
                    <a href="tel:18001234567" style={styles.detailHelpBtn}>
                      <img src={Phone} alt="call" style={{ width: 14, height: 14 }} />
                      <span>Call</span>
                    </a>
                  </div>
                </div>

                {/* Best Quote Alert Banner[cite: 48] */}
                {isQuoteReceived && (
                  <div style={styles.quoteAlertBanner}>
                    <div style={styles.quoteAlertLeft}>
                      <div style={styles.quoteAlertHeader}>
                        <img
                          src={ShootingStar}
                          alt="star"
                          style={{
                            width: 16,
                            height: 16,
                            filter:
                              'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                          }}
                        />
                        <strong style={styles.quoteAlertTitle}>Best Quotation Received</strong>
                      </div>
                      <span style={styles.quoteAlertPrice}>
                        ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    <button
                      type="button"
                      style={styles.quotePayBtn}
                      onClick={() =>
                        alert(
                          `Proceeding to checkout with quote ₹${totalAmount.toLocaleString('en-IN')}`
                        )
                      }
                    >
                      Accept & Lock Price
                    </button>
                  </div>
                )}

                {/* Itemized Specification List[cite: 48] */}
                <div style={styles.itemsSummaryBox}>
                  <div style={styles.itemsHeader}>
                    <span>Items Specification ({activeRfq.total_items || items.length})</span>
                  </div>

                  <div style={styles.itemsList}>
                    {items.map((item, idx) => {
                      const itemName = item.item_name || 'Requested Construction Item';
                      const quantity = item.quantity || 1;
                      const unit = item.UOM || item.uom || 'Units';
                      const unitPrice = Number(item.price_per_unit || item.selling_price || 0);
                      const itemTotal = Number(
                        item.estimated_item_total ||
                          item.total ||
                          item.item_total_after_savings ||
                          unitPrice * quantity
                      );

                      const imageUrl = item.item_image
                        ? `${IMAGE_BASE_URL}${item.item_image}`
                        : null;

                      return (
                        <div key={idx} style={styles.itemRow}>
                          <div style={styles.itemThumbnailBox}>
                            {imageUrl ? (
                              <img src={imageUrl} alt={itemName} style={styles.itemThumbnailImg} />
                            ) : (
                              <img
                                src={ToteIcon}
                                alt="fallback"
                                style={{ width: 22, height: 22, opacity: 0.35 }}
                              />
                            )}
                          </div>

                          <div style={styles.itemMetaCol}>
                            <span style={styles.itemNameText}>{itemName}</span>
                            <span style={styles.itemQuantityText}>
                              Qty: {quantity} {unit}
                              {unitPrice > 0 &&
                                ` · ₹${unitPrice.toLocaleString('en-IN')} / ${unit}`}
                            </span>
                          </div>

                          <div style={styles.itemPriceCol}>
                            <span style={styles.itemTotalText}>
                              ₹{itemTotal.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Estimation Price Breakdown[cite: 48] */}
                  <div style={styles.priceBreakdown}>
                    <div style={styles.breakdownRow}>
                      <span>Subtotal</span>
                      <span>₹{(activeRfq.subtotal || totalAmount).toLocaleString('en-IN')}</span>
                    </div>

                    {Number(activeRfq.tax_amount) > 0 && (
                      <div style={styles.breakdownRow}>
                        <span>Estimated Tax</span>
                        <span>₹{Number(activeRfq.tax_amount).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {Number(activeRfq.delivery_charges) > 0 && (
                      <div style={styles.breakdownRow}>
                        <span>Freight / Logistics</span>
                        <span>₹{Number(activeRfq.delivery_charges).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    <div style={styles.breakdownDivider} />

                    <div style={styles.breakdownTotalRow}>
                      <strong>Estimated Quotation Total</strong>
                      <strong style={{ color: '#C7061F' }}>
                        ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Invoice Reference if Order Finalized[cite: 48] */}
                {activeRfq.order_number && (
                  <div style={styles.invoiceStrip}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={Receipt} alt="receipt" style={{ width: 18, height: 18 }} />
                      <span style={styles.invoiceStripTitle}>Commercial Order Invoice</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={styles.invoiceNumber}>{activeRfq.order_number}</span>
                      <img src={CaretRight} alt="arrow" style={{ width: 14, height: 14 }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    outline: 'none',
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    gap: 20,
    alignItems: 'flex-start',
  },
  masterListPane: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  listSectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#707B8C',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  cardsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: '75vh',
    overflowY: 'auto',
    paddingRight: 4,
  },
  rfqCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  rfqCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rfqIdBadge: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
  },
  statusTag: {
    fontSize: 11,
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: 999,
  },
  rfqCardDate: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  rfqCardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 2,
  },
  rfqCardTitle: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#475569',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
  },
  rfqCardAmount: {
    fontSize: 13.5,
    color: '#2B2A29',
  },
  detailPane: {
    minWidth: 0,
  },
  detailCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '24px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 16,
  },
  detailTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: '800',
    color: '#2B2A29',
  },
  detailDate: {
    fontSize: 12,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  detailActionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  detailHelpBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    textDecoration: 'none',
  },
  quoteAlertBanner: {
    backgroundColor: '#FFF1F2',
    border: '1px solid #FECDD3',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quoteAlertLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  quoteAlertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  quoteAlertTitle: {
    fontSize: 13.5,
    color: '#C7061F',
  },
  quoteAlertPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  quotePayBtn: {
    backgroundColor: '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
  itemsSummaryBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
  },
  itemsHeader: {
    backgroundColor: '#F8F6F2',
    padding: '12px 18px',
    borderBottom: '1px solid #CBD5E1',
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid #F1F5F9',
    gap: 14,
  },
  itemThumbnailBox: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  itemThumbnailImg: {
    width: '90%',
    height: '90%',
    objectFit: 'contain',
  },
  itemMetaCol: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 3,
    minWidth: 0,
  },
  itemNameText: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#2B2A29',
  },
  itemQuantityText: {
    fontSize: 12,
    color: '#707B8C',
  },
  itemPriceCol: {
    flexShrink: 0,
  },
  itemTotalText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2B2A29',
  },
  priceBreakdown: {
    padding: '16px 18px',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#707B8C',
  },
  breakdownDivider: {
    height: 1,
    backgroundColor: '#CBD5E1',
    margin: '4px 0',
  },
  breakdownTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 15,
    color: '#2B2A29',
  },
  invoiceStrip: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invoiceStripTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
  },
  invoiceNumber: {
    fontSize: 12.5,
    color: '#64748B',
  },
  loadingBox: {
    padding: '48px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13.5,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '6px 0 0 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
  detailLoading: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13,
  },
  detailEmpty: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
  },
};